import React from "react";
import { Container, Row, Col } from "reactstrap";
import Helmet from "../components/Helmet/Helmet";
import CommonSection from "../components/UI/CommonSection";

const PrivacyPolicy = () => {
  const privacyText = `
This Privacy Policy explains how White Lions Legacies ("we", "us", "our") collects, uses, stores, shares, and protects information when you visit or use our website (the "Site"), and when you submit an enquiry related to vehicle sales or import-to-order services. We aim to communicate in plain language so you can understand what happens to your information.

By using the Site, you acknowledge that you have read this Privacy Policy. If you do not agree, please do not use the Site or submit information via our forms. If you have questions, contact us using the details in the Contact section at the end of this policy.

1. Who We Are
White Lions Legacies is a vehicle sales and import company based in Gweru CBD, Zimbabwe. We help customers buy vehicles from available stock and source vehicles internationally (including the UK and South Africa) through import-to-order. The Site is primarily an information and enquiry platform used to display product examples, publish helpful guidance, and connect customers to our team.

2. What This Policy Covers
This policy covers personal information processed through the Site and related communications that occur because you interacted with the Site. It does not cover third-party websites or services that may be linked from the Site.

3. Information We Collect
We collect information in three main ways: (a) information you provide directly; (b) information collected automatically when you use the Site; and (c) information we may receive when you contact us through external channels such as phone, WhatsApp, or email.

3.1 Information you provide directly
When you submit an enquiry or fill out a form, you may provide personal information such as your first name and last name, email address, phone number, and the details of what you are looking for. Vehicle preference details can include make, model, year range, budget, preferred color, transmission type, fuel type, mileage expectations, body type, and any required features.

If you request an import-to-order service, you may share additional details to help us search effectively. Examples include acceptable mileage bands, preferred trim levels, engine size preferences, required extras (reverse camera, push-start, infotainment), and whether you want a specific market specification.

We recommend that you do not submit sensitive information (such as identity document numbers, bank account details, card numbers, PINs, or passwords) through the Site unless we specifically request it and provide a secure method. In most situations, we can begin by working with basic contact details and vehicle preferences.

3.2 Information collected automatically
When you use the Site, our systems and your browser may automatically generate technical information. This may include IP address, device type, browser type, operating system, pages visited, approximate location derived from IP address, referral source, and the date/time of visits.

This technical information helps us keep the Site working, monitor performance, and detect suspicious activity. We generally use it in aggregated form (for example, to see which pages are popular). We do not use technical data to attempt to identify you personally unless it is necessary for security or fraud prevention.

3.3 Information from communications
If you contact us via phone call, WhatsApp, SMS, email, or social media, we may keep a record of your messages to respond and maintain continuity. The information we hold depends on what you share and may include your contact details, the content of messages, and any attachments you provide.

4. Why We Use Your Information
We use your information for the following purposes:
(a) to respond to enquiries and provide customer support;
(b) to confirm availability and provide quotations or pricing guidance;
(c) to help source vehicles internationally and communicate options;
(d) to arrange viewing, appointments, and next steps;
(e) to improve the Site and user experience;
(f) to protect the Site and our business against fraud, abuse, and security threats;
(g) to comply with applicable legal obligations.

5. Lawful Basis / Fair Processing
In practice, we process your personal information because you have requested information from us or asked us to take steps to help you. We also process some information for legitimate business interests such as maintaining security, preventing fraud, and improving the Site. In some cases, we may need to process information to comply with legal obligations.

6. Cookies and Similar Technologies
Cookies are small text files stored on your device that help websites function. Depending on hosting and configuration, we may use cookies for essential site operation and to understand traffic patterns.

You can usually control cookies using your browser settings. Disabling cookies may affect certain features, but you should still be able to browse most pages.

7. Sharing and Disclosure
We do not sell your personal information. We share personal information only when necessary to provide services, or when required by law.

7.1 Sharing with suppliers and partners (import-to-order)
When you request import-to-order, we may need to communicate with suppliers, partners, or agents to check availability, confirm pricing, or obtain inspection details. We aim to share only the minimum required information. In many cases, we can obtain availability using vehicle specifications without sharing your full personal details.

7.2 Sharing with service providers
We may use service providers such as hosting providers and infrastructure services to keep the Site running. These providers may process limited technical data as part of their services. We choose reputable providers and limit access where possible.

7.3 Legal requirements
We may disclose information when required by law, a court order, or a lawful request by a competent authority. We may also disclose information to protect our rights, protect users, or prevent fraud.

8. International Transfers
Because we may source vehicles internationally, communications and operational data may be processed by partners outside Zimbabwe. Where international transfers occur, we take reasonable steps to ensure information is handled appropriately.

9. Data Retention
We keep information only as long as necessary for the purposes described. Enquiry records may be kept to follow up, provide continuity of support, and maintain business records. Retention periods vary depending on whether a transaction proceeds.

If you want us to delete your enquiry information, contact us. We will consider requests reasonably, subject to any legal obligations and legitimate recordkeeping requirements.

10. Data Security
We take reasonable measures to protect information, including access controls and limiting access to those who need it. However, no internet transmission is 100% secure. If you believe your information has been compromised, contact us promptly.

11. Your Rights and Choices
You may request access to information we hold about you, request corrections, object to certain processing, request deletion in appropriate circumstances, or request limitations on processing. We may need to verify your identity before acting on requests.

12. Marketing and Updates
We may contact you in response to your enquiry and provide relevant updates. We do not aim to spam. If you do not want follow-up communications, tell us.

13. External Links
The Site may include links to third-party websites. We are not responsible for the privacy practices of third parties. Review their privacy policies before submitting information.

14. Children’s Privacy
The Site is intended for people seeking vehicle sales/import services and is not directed at children. We do not knowingly collect information from children.

15. Changes to This Policy
We may update this policy to reflect operational changes. Updated versions will be posted on this page. Continued use of the Site after updates means you accept the revised policy.

16. Contact
If you have questions or want to make a privacy request, contact us:

CONTACT_BLOCK_COMPONENT

17. Detailed Examples of Data We May Receive
When you enquire about a vehicle, you may share details such as your preferred car model, your budget, whether you want a hybrid, whether you want automatic transmission, and your preferred color. You may also tell us your timeline (for example, whether you want a car urgently). These details are treated as part of your enquiry and help us propose suitable options.

If you ask us to source a vehicle from the UK or South Africa, you may share more detailed preferences such as maximum mileage, acceptable year range, preferred trim level, and any must-have features such as reverse camera, push-start, or 4WD. We treat these details as part of your request and only use them to serve your request.

18. Customer Support and Service Improvement
We may review enquiry patterns to improve how we explain the process and what information we request. For example, if many customers ask similar questions about import timelines, we may update our How It Works page or our blog content. Where we do this, we aim to use aggregated information and not identify individuals.

19. Security Monitoring
We may monitor logs for unusual activity, such as repeated failed access attempts, suspicious traffic spikes, or patterns consistent with automated scraping. This helps protect the Site and our users. Security logs may include IP address and technical request details.

20. Access Controls and Staff Handling
We restrict access to personal information to people who need it to respond to enquiries and provide services. Staff and contractors are expected to treat customer information as confidential and use it only for business purposes.

21. Data Accuracy
We rely on the information you provide. If you provide inaccurate details, it may lead to inaccurate quotations or unsuitable recommendations. You can request that we correct information if you believe it is wrong.

22. Payment Safety Reminder
We do not request that customers submit bank card details, PINs, or passwords through the Site. If you receive a suspicious message claiming to be from us requesting such details, contact us immediately using our official phone numbers. Always verify payment instructions.

23. How to Make a Privacy Request
If you want to request access, correction, deletion, or limitation of processing, contact us and explain:
(a) what information you are requesting,
(b) your relationship with us (for example, you submitted an enquiry on a certain date),
(c) the best way for us to verify your identity.

We may ask additional questions to confirm we are responding to the correct person.

24. Retention Guidance (Practical)
Enquiry messages may be retained for business continuity so that if you contact us again, we understand what was discussed. If you do not proceed with a transaction, we may still keep a record for a reasonable period in case you return. If you proceed with a transaction, records may be kept longer to support documentation and support.

25. Updates and Version Control
When we update this Privacy Policy, we will publish the updated version on this page. Significant changes may include changes to how we collect technical data, the service providers we use, or changes to how enquiries are handled.

26. Website Hosting and Logs
Like most modern websites, our hosting environment may produce standard server logs. These logs can include the time of a request, the page requested, the status code returned, the device and browser user-agent string, and the IP address. These logs are typically used for security monitoring, troubleshooting, and performance tuning.

We do not use server logs to profile individuals for advertising. Where we review logs, it is generally to identify errors or suspicious activity.

27. Contact Forms and Message Content
If you submit a message through an enquiry form, the content of the message is stored so our team can read and respond. You are responsible for what you choose to write. Avoid sharing unnecessary sensitive personal information. If you accidentally submit sensitive information, contact us and request deletion where appropriate.

28. Device Storage and Browser Features
Your browser may store local data such as cached files to speed up page loads. This is controlled by your browser. You may clear your browser cache to remove cached assets.

29. Do Not Track Signals
Some browsers provide “Do Not Track” signals. Because there is no consistent industry standard for how to respond, we may not respond to all Do Not Track requests. You can manage privacy via browser controls and by limiting what you submit.

30. Business Transfers
If our business structure changes (for example, business sale, merger, or restructuring), enquiry records may be transferred as part of the business operations. If such a transfer occurs, we will aim to ensure the receiving party continues to protect your information.

31. Operational Communications
If you submit an enquiry, we may contact you to:
(a) ask clarifying questions,
(b) confirm vehicle details,
(c) provide a quotation,
(d) provide updates about sourcing,
(e) arrange an appointment.

These communications are considered part of customer support. If you do not want follow-ups, you may request that we stop, but note that it may limit our ability to serve your request.

32. Data Minimisation
We aim to collect only what is necessary. For example, to respond to a basic enquiry we typically need name, phone or email, and vehicle preference details. We do not need passwords or PINs.

33. Accuracy and Updates
If your requirements change (for example, your budget, preferred model, or preferred color), you can send an updated message. This helps us keep our records accurate and reduces misunderstandings.

34. Complaints
If you have concerns about how your information is handled, contact us using the Contact section above. Provide details of your concern and we will review it.

35. Categories of Data (More Detail)
To make this policy clear, we group information into categories:
(a) Identity and contact data: name, phone number, email address.
(b) Enquiry and preference data: the vehicle you want, budget range, preferred color, year range, mileage preference, transmission, fuel type, and notes you type into enquiry forms.
(c) Technical data: IP address, device type, browser type, operating system, pages visited, approximate location derived from IP address, and timestamps.
(d) Communication data: messages exchanged with our team through WhatsApp, SMS, email, or phone call notes.
(e) Transaction-related data (only if you proceed): documents or details needed for a specific transaction, such as clearance or registration support.

We do not intentionally collect special category data (for example, health data) through the Site. Please do not submit such information.

36. Lawful Bases Explained (Plain Language)
When we say we process information based on “legitimate interests”, it means we have a business reason that does not override your privacy rights, such as responding to enquiries, preventing fraud, and keeping the Site secure.

When we say processing is “necessary to take steps before entering into a transaction”, it means you asked us to quote or source a vehicle, and we need your information to do that.

When we say “legal obligation”, it means there may be laws or regulations that require us to keep certain records.

37. Consent and Withdrawals
In many cases, we do not rely on consent because you are actively requesting our services. However, if there is a situation where we rely on consent (for example, a specific marketing message), you can withdraw your consent by contacting us. Withdrawal does not affect processing that was already lawful.

38. Analytics and Aggregated Insights
We may look at aggregated traffic patterns to understand which pages are most useful (for example, how many people read How It Works, or how many people search for a certain model). We use this to improve the Site. Aggregated analytics is not designed to identify you.

39. Storage Locations
Information may be stored in:
(a) email inboxes used for customer support,
(b) messaging histories (e.g., WhatsApp business communication),
(c) website hosting logs,
(d) internal recordkeeping documents.

Access is limited to people who need it to respond to customers and run the business.

40. Security Practices (Practical)
We use reasonable security practices such as:
(a) limiting access to authorised staff,
(b) using passwords and device security,
(c) keeping software updated where possible,
(d) reviewing suspicious activity.

However, no system is perfectly secure. You should also protect your own devices and avoid sharing sensitive banking details through unverified channels.

41. Data Breach Response
If we become aware of a serious security incident that affects your personal information, we will take reasonable steps to investigate, limit harm, and communicate where appropriate.

42. Third-Party Requests and Scams
Be aware of impersonation scams. We will not ask for PINs or passwords. If someone claims to be us and requests sensitive data, contact us using our official numbers and email to verify.

43. How To Update Your Preferences
If your requirements change (for example, you change your budget or preferred color), you can message us with the updated information. This helps reduce misunderstandings.

44. Retention Schedule Examples
Retention depends on what you did:
(a) Basic enquiry with no follow-up: we may retain it for a reasonable period so we can respond if you return.
(b) Ongoing sourcing conversation: we may retain messages longer to track decisions and requirements.
(c) Completed transaction: records may be retained longer for documentation and support.

If you want deletion, contact us and we will consider your request.

45. Cross-Border Communications
If you request import-to-order, we may communicate with suppliers outside Zimbabwe. We try to share only what is necessary, often the vehicle specification rather than full personal details.

46. Your Responsibilities
You should:
(a) provide accurate information,
(b) avoid submitting unnecessary sensitive information,
(c) verify payment instructions,
(d) keep your contact details updated.

47. Policy Summary
We collect information to respond to enquiries, source vehicles, improve the Site, and keep operations secure. We do not sell personal information. You can contact us to request access, correction, or deletion.
  `.trim();

  const paragraphs = privacyText.split("\n\n");

  const contactBlock = (
    <>
      <p className="section__description mb-0">White Lions Legacies</p>
      <p className="section__description mb-0">Location: Gweru CBD, Zimbabwe</p>
      <p className="section__description mb-0">
        Email: <a href="mailto:contact@whitelionslegacies.com">contact@whitelionslegacies.com</a>
      </p>
      <p className="section__description mb-0">
        Phone: <a href="tel:+263782528050">+263 78 252 8050</a> /{" "}
        <a href="tel:+263716264988">+263 71 626 4988</a>
      </p>
    </>
  );

  return (
    <Helmet title="Privacy Policy">
      <CommonSection title="Privacy Policy" />
      <section>
        <Container>
          <Row className="justify-content-center">
            <Col lg="10">
              {paragraphs.map((text, idx) => {
                if (text.trim() === "CONTACT_BLOCK_COMPONENT") {
                  return <React.Fragment key={idx}>{contactBlock}</React.Fragment>;
                }

                return (
                  <p key={idx} className="section__description">
                    {text}
                  </p>
                );
              })}
            </Col>
          </Row>
        </Container>
      </section>
    </Helmet>
  );
};

export default PrivacyPolicy;
